import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Accordion,
  AccordionItem,
  type ActionData,
  Details,
  type DetailsProps,
  Divider,
  DynamicDataList,
  type FormActionData,
  formatDateTime,
  GenericField,
  getFormDiff,
  IconName,
  InfoPanel,
  List,
  Loader,
  type ObjectSchemaDefinition,
  Paragraph,
  ReadOnlyField,
  Section,
  SingleLineTextField,
  type StationMessage,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { config } from 'config';
import { Field } from 'formik';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';
import type { ErrorWithGraphQLErrors } from '../../../../common/errors/assert-custom-errors';
import { ensureError } from '../../../../common/errors/ensure-error';
import {
  CustomizableServiceCrumbDataDocument,
  type CustomizableServiceCrumbDataQuery,
  CustomizableServiceDocument,
  type CustomizableServiceQuery,
  type CustomizableServiceQueryVariables,
  DeleteServiceDefinitionDocument,
  type DeleteServiceDefinitionMutation,
  type DeleteServiceDefinitionMutationVariables,
  DeployServiceDocument,
  type DeployServiceMutation,
  type DeployServiceMutationVariables,
  ErrorCodesEnum,
  ReDeploymentServiceDocument,
  type ReDeploymentServiceMutation,
  type ReDeploymentServiceMutationVariables,
  RepositoryTagsDocument,
  type RepositoryTagsQuery,
  type RepositoryTagsQueryVariables,
  ResourceStatus,
  SetDatabaseAccessDocument,
  type SetDatabaseAccessMutation,
  type SetDatabaseAccessMutationVariables,
  SetServiceExpirationDateDocument,
  type SetServiceExpirationDateMutation,
  type SetServiceExpirationDateMutationVariables,
  UndeployServiceDocument,
  type UndeployServiceMutation,
  type UndeployServiceMutationVariables,
  UpdateServiceDefinitionDocument,
  type UpdateServiceDefinitionMutation,
  type UpdateServiceDefinitionMutationVariables,
} from '../../../../generated/hosting-service';
import { environmentApolloClient } from '../../../environmentApolloClient';
import { LabeledIconField } from '../common/components/LabeledIconField/LabeledIconField';
import { getServiceDeploymentStatusIconLabel } from '../common/utils';
import classes from './CustomizableServiceDetails.module.scss';
import {
  generateCurrentDeploymentColumns,
  generateUpgradeAvailabilityColumns,
  type LastDeployment,
} from './CustomizableServiceFormComponents';

type CustomizableService = NonNullable<
  CustomizableServiceQuery['serviceDefinition']
>;

/** Placeholder anchor: the database access page is not published yet. */
const databaseAccessDocsLink = (
  <a
    href="https://docs.axinom.com/platform/packages/mosaic-cli#mosaic-hosting-database-proxy"
    target="_blank"
    rel="noreferrer"
  >
    Mosaic Documentation
  </a>
);

/** The flag is a timestamp, so "enabled" means set and not yet elapsed. */
const isDatabaseAccessOpen = (expiresAt: string | null): boolean =>
  expiresAt !== null && new Date(expiresAt).getTime() > Date.now();

type RepositoryTags = NonNullable<
  RepositoryTagsQuery['serviceDefinition']
>['repositoryTags'];

interface ApiUrl {
  urlName: string;
}

const customizableServiceDetailsSchema = yup.object<ObjectSchemaDefinition>({
  name: yup.string().required('Service Name is required'),
  repositoryName: yup.string(),
  customDnsServiceLabel: yup
    .string()
    .nullable()
    .max(
      47,
      `The custom DNS service label cannot be longer than 47 characters.`,
    )
    .matches(
      /^[a-z0-9-]*$/,
      'The custom DNS service label must contain only lowercase alphanumeric characters and dashes.',
    ),
});

export const CustomizableServiceDetails: React.FC = () => {
  const history = useHistory();

  const { envId, customizableServiceId } = useParams<{
    envId: string;
    customizableServiceId: string;
  }>();

  const [customizableServiceData, setCustomizableServiceData] =
    useState<CustomizableService>();
  const [repositoryTags, setRepositoryTags] = useState<RepositoryTags>();
  const [lastDeploymentData, setLastDeploymentData] =
    useState<LastDeployment[]>();
  const [generalStationMessage, setGeneralStationMessage] =
    useState<StationMessage>();
  const [deploymentError, setDeploymentError] = useState<StationMessage>();
  const [serviceUpgradeTarget, setServiceUpgradeTarget] =
    useState<LastDeployment[]>();
  const [error, setError] = useState<string>();
  const [
    isLatestVersionAlreadyDeployed,
    setLatestVersionAlreadyDeployedStatus,
  ] = useState<boolean>();

  useEffect(() => {
    (async () => {
      try {
        const envApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
        );

        const customizableServiceResult = await envApolloClient.query<
          CustomizableServiceQuery,
          CustomizableServiceQueryVariables
        >({
          query: CustomizableServiceDocument,
          variables: {
            id: customizableServiceId,
          },
          fetchPolicy: 'no-cache',
        });

        const customizableServiceData =
          customizableServiceResult.data.serviceDefinition;

        if (customizableServiceData) {
          setCustomizableServiceData(customizableServiceData);

          if (
            customizableServiceData.ownedByAxinom === false &&
            customizableServiceData.repositoryName === ''
          ) {
            setGeneralStationMessage({
              title:
                'Repository Name must be updated to reflect the correct value that is used by your container registry. Some features such as listing container images and deploying the service will not work until this value is updated to the correct value.',
              type: 'info',
            });
          }

          const lastDeployment = {
            ...customizableServiceData.serviceDeployments.nodes[0],
            servicePiletArtifacts:
              customizableServiceData.serviceDeployments.nodes[0]
                ?.servicePiletArtifacts ?? '',
            manifest:
              (customizableServiceData?.ownedByAxinom
                ? formatDateTime(
                    customizableServiceData.serviceDeployments.nodes[0]
                      ?.serviceDeploymentManifest?.createdDate,
                  )
                : customizableServiceData.serviceDeployments.nodes[0]
                    ?.serviceDeploymentManifest?.name) ?? '',
          };

          setLastDeploymentData([lastDeployment]);

          if (customizableServiceData?.ownedByAxinom && !repositoryTags) {
            const repositoryTagsResult = await envApolloClient.query<
              RepositoryTagsQuery,
              RepositoryTagsQueryVariables
            >({
              query: RepositoryTagsDocument,
              variables: {
                id: customizableServiceId,
              },
              fetchPolicy: 'no-cache',
            });
            setRepositoryTags(
              repositoryTagsResult.data.serviceDefinition?.repositoryTags,
            );
          }

          const activeDeployment =
            customizableServiceData.serviceDeployments.nodes[0];

          const latestAvailableServiceVersion = {
            containerImageVersion: repositoryTags && repositoryTags[0].tag,
            serviceDeploymentManifest: {
              id: customizableServiceData.serviceDeploymentManifest.nodes[0]
                ?.id,
              createdDate:
                customizableServiceData.serviceDeploymentManifest.nodes[0]
                  ?.createdDate,
              name: customizableServiceData.serviceDeploymentManifest.nodes[0]
                ?.name,
            },
            servicePiletArtifacts:
              customizableServiceData.servicePiletArtifacts.nodes,
          };

          if (
            latestAvailableServiceVersion.containerImageVersion !==
              activeDeployment?.containerImageVersion ||
            latestAvailableServiceVersion.serviceDeploymentManifest.id !==
              activeDeployment?.serviceDeploymentManifest?.id ||
            !latestAvailableServiceVersion.servicePiletArtifacts.every(
              (artifact) =>
                activeDeployment?.servicePiletArtifactIds.includes(artifact.id),
            ) ||
            !activeDeployment
          ) {
            const upgradeService = {
              ...customizableServiceData.serviceDeployments.nodes[0],
              containerImageVersion:
                latestAvailableServiceVersion.containerImageVersion,
              manifest:
                formatDateTime(
                  latestAvailableServiceVersion.serviceDeploymentManifest
                    .createdDate,
                ) ?? '',
              servicePiletArtifacts:
                latestAvailableServiceVersion.servicePiletArtifacts.length > 0
                  ? latestAvailableServiceVersion.servicePiletArtifacts
                      .map(
                        (artifact) =>
                          `${artifact.name}@${artifact.packageVersion}`,
                      )
                      .join(', ')
                  : `None`,
            };

            setServiceUpgradeTarget([upgradeService]);
            setLatestVersionAlreadyDeployedStatus(false);
          } else if (
            latestAvailableServiceVersion.serviceDeploymentManifest.id ===
            activeDeployment.serviceDeploymentManifest?.id
          ) {
            setLatestVersionAlreadyDeployedStatus(true);
          } else {
            setServiceUpgradeTarget(undefined);
            setLatestVersionAlreadyDeployedStatus(false);
          }

          if (activeDeployment) {
            if (activeDeployment.status === ResourceStatus.CreatePending) {
              setGeneralStationMessage({
                title:
                  'The service is now being deployed, and this operation may take some minutes to complete. Please come back to this station or refresh to check if the deployment is successful.',
                type: 'info',
              });
            } else if (
              activeDeployment.status === ResourceStatus.DeletePending
            ) {
              setGeneralStationMessage({
                title:
                  'The service is now being undeployed, and this operation may take some minutes to complete. Please come back to this station or refresh to check if the undeployment is successful.',
                type: 'info',
              });
            } else {
              setGeneralStationMessage(undefined);
            }

            if (activeDeployment.status === ResourceStatus.CreateError) {
              // TODO: Update message in future to view deployment-logs for more details
              setDeploymentError({
                type: 'warning',
                title: `Service deployment has failed with the following error (check deployment logs for details): ${activeDeployment.errorMessage}`,
              });
            }

            if (activeDeployment.status === ResourceStatus.DeleteError) {
              // TODO: Update message in future to view deployment-logs for more details
              setDeploymentError({
                type: 'warning',
                title: `Service undeployment has failed with the following error (check deployment logs for details): ${activeDeployment.errorMessage} Please deploy the service again to attempt the redeploy.`,
              });
            }

            if (lastDeployment && customizableServiceData?.ownedByAxinom) {
              // Get expiration date
              const expireDate = new Date(
                customizableServiceData.evaluationPodExpirationDate,
              );

              // Calculate the remaining days count from the current date to the expiration date
              const currentDate = new Date();
              const timeDifference =
                expireDate.getTime() - currentDate.getTime();
              const remainingDays = Math.ceil(
                timeDifference / (1000 * 60 * 60 * 24),
              );

              const summary =
                'Request DB Export of evaluation service deployment';

              const description = `Hi! Please provide me a DB export for my evaluation service deployment based on the following details

Tenant ID: ${customizableServiceData.tenantId}
Environment ID: ${customizableServiceData.environmentId}
Service Definition ID: ${customizableServiceData.id} 
Service ID: ${customizableServiceData.serviceId}`;

              const supportBaseUrl = new URL(
                `?summary=${summary}&description=${encodeURIComponent(
                  description,
                )}`,
                config.axinomProductSupportBaseUrl,
              ).toString();

              if (
                activeDeployment.status === ResourceStatus.Ready &&
                remainingDays <= 0
              ) {
                setGeneralStationMessage({
                  title: (
                    <span>
                      The service is automatically scaled down. You may use the
                      Deploy action to test the service again. In case you need
                      to export the data you can request it from{' '}
                      <a href={supportBaseUrl} target="_blank" rel="noreferrer">
                        axinom support
                      </a>
                      .
                    </span>
                  ),
                  type: 'info',
                });
              } else if (
                activeDeployment.status === ResourceStatus.Ready &&
                remainingDays <= config.expirationWarningDuration
              ) {
                const extendExpireDate = async (
                  remainingDays: number,
                ): Promise<void> => {
                  try {
                    const currentDate = new Date();
                    const expirationDate = new Date(currentDate);

                    if (
                      remainingDays <= config.expirationWarningDuration &&
                      remainingDays > 0
                    ) {
                      expirationDate.setDate(
                        currentDate.getDate() +
                          config.evaluationExpirationExtensionInDays +
                          remainingDays,
                      );
                    } else {
                      expirationDate.setDate(
                        currentDate.getDate() +
                          config.evaluationExpirationExtensionInDays,
                      );
                    }

                    const envApolloClient = environmentApolloClient(
                      envId,
                      new URL(
                        `/graphql`,
                        config.hostingServiceAdminBaseUrl,
                      ).toString(),
                    );
                    if (lastDeployment) {
                      await envApolloClient?.mutate<
                        SetServiceExpirationDateMutation,
                        SetServiceExpirationDateMutationVariables
                      >({
                        mutation: SetServiceExpirationDateDocument,
                        variables: {
                          input: {
                            serviceDefinitionId: customizableServiceData?.id,
                            expirationDate: expirationDate,
                          },
                        },
                      });
                    }
                    history.replace(history.location.pathname);
                  } catch (e) {
                    const error = ensureError(e);
                    setError(error.message);
                  }
                };

                setGeneralStationMessage({
                  title: (
                    <span>
                      You are using an evaluation setup of the template service.
                      The service will be scaling down automatically in{' '}
                      {remainingDays} days. You can prolong your evaluation by
                      clicking{' '}
                      <a
                        href="#"
                        onClick={() => extendExpireDate(remainingDays)}
                      >
                        here
                      </a>
                      .
                    </span>
                  ),
                  type: 'info',
                });
              } else if (activeDeployment.status === ResourceStatus.Ready) {
                setGeneralStationMessage({
                  title: `You are using an evaluation setup of the template service. The service will be scaled down automatically in ${remainingDays} days`,
                  type: 'info',
                });
              }
            }
          } else {
            if (customizableServiceData?.ownedByAxinom) {
              setGeneralStationMessage({
                title: `This is a template service for evaluation purposes. Once deployed, it will automatically scale down in ${config.evaluationExpirationDuration} days. A link to prolong the evaluation will appear ${config.expirationWarningDuration} days before scale-down.`,
                type: 'info',
              });
            }
          }

          // Set last: the deployment branches above clear the message, and an
          // open path into the database outranks them while it lasts.
          if (
            isDatabaseAccessOpen(
              customizableServiceData.databaseAccessExpiresAt,
            )
          ) {
            setGeneralStationMessage({
              title: (
                <span>
                  Database access is open for this service until{' '}
                  {formatDateTime(
                    customizableServiceData.databaseAccessExpiresAt,
                  )}
                  . It will close automatically at that time. Refer to{' '}
                  {databaseAccessDocsLink} for more information.
                </span>
              ),
              type: 'warning',
            });
          }
        } else {
          setCustomizableServiceData(undefined);
          throw Error('The Customizable Service does not exist');
        }

        if (customizableServiceResult.error !== undefined) {
          setError(customizableServiceResult.error.message);
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      }
    })();
  }, [customizableServiceId, envId, repositoryTags, history]);

  const { actions } = useActions(
    envId,
    customizableServiceId,
    customizableServiceData,
    repositoryTags,
    isLatestVersionAlreadyDeployed,
  );

  const onSubmit = useCallback(
    async (
      formData: CustomizableService,
      initialData: DetailsProps<CustomizableService>['initialData'],
    ): Promise<void> => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
      );

      // If the customDnsServiceLabel is empty, set it to NULL
      if (formData.customDnsServiceLabel === '') {
        formData.customDnsServiceLabel = null;
      }

      try {
        await envApolloClient.mutate<
          UpdateServiceDefinitionMutation,
          UpdateServiceDefinitionMutationVariables
        >({
          mutation: UpdateServiceDefinitionDocument,
          variables: {
            input: {
              id: customizableServiceId,
              patch: getFormDiff(formData, initialData.data),
            },
          },
          fetchPolicy: 'no-cache',
        });
      } catch (error) {
        /**
         * We make an assumption here if the `DatabaseValidationFailed` is thrown that it must be due to
         * the custom DNS service label being a duplicate.
         *
         * TODO: We should have a better way to handle this in the future specially when the form can
         * contain multiple fields that individually can have a unique constraint.
         */
        if (
          (error as ErrorWithGraphQLErrors).graphQLErrors &&
          (error as ErrorWithGraphQLErrors).graphQLErrors?.[0].code ===
            ErrorCodesEnum.DatabaseValidationFailed &&
          (error as ErrorWithGraphQLErrors).graphQLErrors?.[0].message ===
            'Attempt to create or update an element failed, as it would have resulted in a duplicate element.'
        ) {
          (
            error as Error
          ).message = `The specified custom DNS service label is already in use. Note that the value must be globally unique. Please specify a different value and try again.`;
          throw error;
        } else {
          /**
           * Unknown error, so we bubble it up as-is without any beautification of the message so that the
           * user to have a look and take necessary actions.
           */
          throw error;
        }
      }
    },
    [customizableServiceId, envId],
  );

  return (
    <Details<CustomizableService>
      defaultTitle="Customizable Service Details"
      titleProperty={customizableServiceData ? 'name' : undefined}
      actions={customizableServiceData ? actions : undefined}
      initialData={{
        data: customizableServiceData,
        loading: false, // TODO: if/when mosaic-ui has a way to render a standard loading animation for detail stations, we need to set this value to (customizableServiceData === undefined)
        error: error,
      }}
      saveData={onSubmit}
      validationSchema={customizableServiceDetailsSchema}
      infoPanel={
        customizableServiceData ? (
          <Panel customizableServiceData={customizableServiceData} />
        ) : undefined
      }
      stationMessage={generalStationMessage ?? deploymentError}
    >
      <Form
        customizableServiceData={customizableServiceData}
        serviceUpgradeTarget={serviceUpgradeTarget}
        lastDeploymentData={lastDeploymentData ?? []}
        envId={envId}
        customizableServiceId={customizableServiceId}
        isLatestVersionAlreadyDeployed={isLatestVersionAlreadyDeployed}
      />
    </Details>
  );
};

const Form: React.FC<{
  customizableServiceData: CustomizableService | undefined;
  serviceUpgradeTarget: LastDeployment[] | undefined;
  lastDeploymentData: LastDeployment[];
  envId: string;
  customizableServiceId: string;
  isLatestVersionAlreadyDeployed: boolean | undefined;
}> = ({
  customizableServiceData,
  serviceUpgradeTarget,
  lastDeploymentData,
  envId,
  customizableServiceId,
  isLatestVersionAlreadyDeployed,
}) => {
  const history = useHistory();

  const activeDeployment = customizableServiceData?.serviceDeployments.nodes[0];

  // TODO: Remove this patch once the backend is updated to handle the `ScaledDown` status
  const serviceState =
    customizableServiceData?.ownedByAxinom &&
    activeDeployment?.status === ResourceStatus.Ready &&
    customizableServiceData?.evaluationPodExpirationDate === null
      ? ResourceStatus.ScaledDown
      : activeDeployment?.status;

  const { icon, label } = getServiceDeploymentStatusIconLabel(serviceState);

  /* TODO: Handle when we have more than one DNS for a service. We should show each URL on a new line then. */
  const generateAPIUrls = (): ApiUrl[] => {
    const urlArray: ApiUrl[] = [];
    activeDeployment?.dnsRecords.nodes.map((dnsRecord) => {
      const url = {
        urlName:
          `https://${dnsRecord.fqdn}` +
          (activeDeployment.serviceDefinition?.ownedByAxinom
            ? `/graphiql`
            : ''),
      };
      urlArray.push(url);
    });
    return urlArray;
  };

  return (
    <Loader showLoader={customizableServiceData === undefined}>
      <>
        <Field
          name="name"
          label="Service Name"
          as={
            !customizableServiceData?.ownedByAxinom
              ? SingleLineTextField
              : ReadOnlyField
          }
        />
        {!customizableServiceData?.ownedByAxinom && (
          <>
            <Field
              name="repositoryName"
              label="Repository Name"
              tooltipContent={`The repository name where the container images of this service can be found. You can find the repository name from the Container Registry you have configured for this environment. Example: "user-name/service-name". When a new deployment is made, this repository name will be used to pull the container image.`}
              as={SingleLineTextField}
            />

            <Field
              name="customDnsServiceLabel"
              label="Custom DNS Service Label"
              tooltipContent={`If specified, this globally unique value will be used to generate the service specific subdomain label of the DNS record (i.e. <dnsPortName>-<customServiceLabel>.<shared-dns-suffix>). If left empty, a unique value will be auto-generated. It is recommended to fill a value to ensure that the DNS remains constant. Any changes made to the value will be effective post deployment.`}
              as={SingleLineTextField}
            />
          </>
        )}
        <LabeledIconField
          name="status"
          label="Status"
          icon={icon}
          value={label}
        />

        {customizableServiceData?.ownedByAxinom ? (
          <GenericField label="Deployment Information" name="lastDeployment">
            <Accordion alignAccordionItem={false}>
              {customizableServiceData?.serviceDeployments.nodes.length && (
                <AccordionItem
                  header={
                    <div className={clsx(classes.row)}>
                      <span>Current Deployment Details</span>
                    </div>
                  }
                >
                  <div className={clsx(classes.content)}>
                    <List<LastDeployment>
                      columns={generateCurrentDeploymentColumns(true)}
                      data={lastDeploymentData}
                      showActionButton={false}
                    />
                  </div>
                </AccordionItem>
              )}

              <AccordionItem
                header={
                  customizableServiceData?.serviceDeployments?.nodes.length ===
                  0 ? (
                    <div className={clsx(classes.row)}>
                      <span>Available Deployment Details</span>
                    </div>
                  ) : (
                    <div className={clsx(classes.infoPanelRow)}>
                      <span>Upgrade Information</span>
                      {isLatestVersionAlreadyDeployed === undefined ? (
                        <span className={clsx(classes.colRight)}>
                          <Loader showLoader={true} />
                          &nbsp;
                        </span>
                      ) : isLatestVersionAlreadyDeployed ? (
                        <span className={clsx(classes.colRight)}>
                          Up to date &nbsp;
                        </span>
                      ) : (
                        <span className={clsx(classes.colRight)}>
                          Upgrade Availabile &nbsp;
                        </span>
                      )}
                    </div>
                  )
                }
              >
                <div className={clsx(classes.content)}>
                  <>
                    {isLatestVersionAlreadyDeployed ? (
                      <p>
                        {'Current deployment is already the latest available'}
                      </p>
                    ) : (
                      <List<LastDeployment>
                        columns={generateUpgradeAvailabilityColumns()}
                        data={serviceUpgradeTarget ?? []}
                        showActionButton={false}
                        isLoading={serviceUpgradeTarget === undefined}
                      />
                    )}
                  </>
                </div>
              </AccordionItem>
            </Accordion>
          </GenericField>
        ) : (
          <>
            {customizableServiceData?.serviceDeployments.nodes.length ? (
              <GenericField
                label="Deployment Information"
                name="lastDeployment"
              >
                <List<LastDeployment>
                  columns={generateCurrentDeploymentColumns(
                    customizableServiceData?.ownedByAxinom,
                  )}
                  data={lastDeploymentData}
                  onItemClicked={(item) => {
                    !customizableServiceData?.ownedByAxinom
                      ? history.push(
                          `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${customizableServiceId}/deployments/${item.id}`,
                        )
                      : '';
                  }}
                  showActionButton={!customizableServiceData?.ownedByAxinom}
                />
              </GenericField>
            ) : (
              ''
            )}
          </>
        )}

        {customizableServiceData?.serviceDeployments.nodes[0]?.status ===
          ResourceStatus.Ready && (
          <>
            <Divider />
            <GenericField label="API URL(s)" name="apiUrls">
              {activeDeployment?.dnsRecords.nodes.length ? (
                <>
                  <DynamicDataList<ApiUrl>
                    columns={[
                      {
                        propertyName: 'urlName',
                        label: 'URL',
                      },
                    ]}
                    showHeader={false}
                    value={generateAPIUrls()}
                    allowReordering={true}
                    inlineMenuActions={useGenerateInlineMenuActions}
                  />
                </>
              ) : (
                <p>{'No URL(s) defined'}</p>
              )}
            </GenericField>
          </>
        )}
      </>
    </Loader>
  );
};
const useGenerateInlineMenuActions: (data: ApiUrl) => ActionData[] = (data) => {
  return [
    {
      label: 'Open URL',
      path: { pathname: data?.urlName },
      openInNewTab: true,
    },
    {
      label: 'Copy URL',
      icon: IconName.Copy,
      onActionSelected: () => {
        try {
          // copy url to clipboard
          navigator.clipboard.writeText(data?.urlName ?? '');
        } catch (_error) {
          // eslint-disable-next-line no-console
          console.warn('Failed to copy to clipboard');
        }
      },
    },
  ];
};

const Panel: React.FC<{ customizableServiceData: CustomizableService }> = ({
  customizableServiceData,
}) => {
  const activeDeployment = customizableServiceData.serviceDeployments.nodes[0];
  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Service Definition ID">
          {customizableServiceData.id}
        </Paragraph>
        <Paragraph title="Service ID">
          {customizableServiceData.serviceId}
        </Paragraph>
        <Paragraph title="Owned by Axinom">
          {customizableServiceData.ownedByAxinom ? 'Yes' : 'No'}
        </Paragraph>
        <Paragraph title="Last Modified">
          {activeDeployment
            ? `${formatDateTime(activeDeployment.updatedDate)} by ${
                activeDeployment.createdUser
              }`
            : `${formatDateTime(customizableServiceData.updatedDate)} by ${
                customizableServiceData.updatedUser
              }`}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Environment ID">
          {customizableServiceData.environmentId}
        </Paragraph>
        <Paragraph title="Tenant ID">
          {customizableServiceData.tenantId}
        </Paragraph>
      </Section>
    </InfoPanel>
  );
};

function useActions(
  envId: string,
  serviceDefinitionId: string,
  data: CustomizableService | undefined,
  repositoryTags: RepositoryTags | undefined,
  isLatestVersionAlreadyDeployed: boolean | undefined,
): {
  readonly actions: FormActionData<CustomizableService>[];
} {
  const history = useHistory();
  const envApolloClient = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
  );
  const actualServiceStatus = data?.serviceDeployments?.nodes[0]?.status;
  const isAxinomOwnedService = data?.ownedByAxinom ?? false;
  const databaseAccessOpen = isDatabaseAccessOpen(
    data?.databaseAccessExpiresAt ?? null,
  );
  // The database comes with the deployment, so there is nothing to open access
  // to until then. A scaled-down service is stored as READY, so it still counts.
  const isDeployed = actualServiceStatus === ResourceStatus.Ready;

  const setDatabaseAccess = async (enabled: boolean): Promise<void> => {
    await envApolloClient.mutate<
      SetDatabaseAccessMutation,
      SetDatabaseAccessMutationVariables
    >({
      mutation: SetDatabaseAccessDocument,
      variables: { input: { serviceDefinitionId, enabled } },
      fetchPolicy: 'no-cache',
    });
    history.replace(history.location.pathname);
  };

  const actions: FormActionData<CustomizableService>[] = [
    ...getActionsIf(
      isAxinomOwnedService &&
        repositoryTags !== undefined &&
        repositoryTags.length > 0 &&
        (actualServiceStatus === undefined ||
          actualServiceStatus === ResourceStatus.DeleteCompleted ||
          actualServiceStatus === ResourceStatus.CreateError ||
          actualServiceStatus === ResourceStatus.DeleteError),
      {
        label: 'Deploy',
        confirmationMode: 'Advanced',
        confirmationConfig: {
          body: `This will create the relevant resources and initiate the deployment of ${data?.name} to the environment. Please confirm to proceed.`,
        },
        onActionSelected: async () => {
          await envApolloClient.mutate<
            DeployServiceMutation,
            DeployServiceMutationVariables
          >({
            mutation: DeployServiceDocument,
            variables: {
              input: {
                serviceDefinitionId,
                containerImageVersion: repositoryTags
                  ? repositoryTags[0]?.tag
                  : 'NO_TAG',
                serviceDeploymentConfigurationId:
                  data?.serviceDeploymentManifest?.nodes[0]?.id ?? '',
                servicePiletArtifactIds:
                  data?.servicePiletArtifacts.nodes.map(
                    (piletArtifact) => piletArtifact?.id,
                  ) ?? [],
              },
            },
            fetchPolicy: 'no-cache',
          });
          history.replace(history.location.pathname);
        },
      },
    ),
    ...getActionsIf(
      isAxinomOwnedService && actualServiceStatus === ResourceStatus.Ready,
      {
        label: 'Redeploy',
        confirmationMode: 'Advanced',
        confirmationConfig: {
          body: `This will redeploy the ${data?.name} to the environment. Please confirm to proceed.`,
        },
        onActionSelected: async () => {
          await envApolloClient.mutate<
            ReDeploymentServiceMutation,
            ReDeploymentServiceMutationVariables
          >({
            mutation: ReDeploymentServiceDocument,
            variables: {
              input: {
                serviceDeploymentId: data?.serviceDeployments.nodes[0]?.id,
              },
            },
            fetchPolicy: 'no-cache',
          });
          history.replace(history.location.pathname);
        },
      },
    ),
    ...getActionsIf(
      isAxinomOwnedService &&
        isLatestVersionAlreadyDeployed !== undefined &&
        isLatestVersionAlreadyDeployed === false &&
        actualServiceStatus === ResourceStatus.Ready,
      {
        label: 'Upgrade',
        confirmationMode: 'Advanced',
        confirmationConfig: {
          body: 'Upgrade Available. You can upgrade deployment details to the latest available version. Please confirm to proceed.',
        },
        onActionSelected: async () => {
          await envApolloClient.mutate<
            DeployServiceMutation,
            DeployServiceMutationVariables
          >({
            mutation: DeployServiceDocument,
            variables: {
              input: {
                serviceDefinitionId,
                containerImageVersion: repositoryTags
                  ? repositoryTags[0]?.tag
                  : 'NO_TAG',
                serviceDeploymentConfigurationId:
                  data?.serviceDeploymentManifest?.nodes[0]?.id ?? '',
                servicePiletArtifactIds:
                  data?.servicePiletArtifacts.nodes.map(
                    (piletArtifact) => piletArtifact?.id,
                  ) ?? [],
              },
            },
            fetchPolicy: 'no-cache',
          });
          history.replace(history.location.pathname);
        },
      },
    ),
    ...getActionsIf(
      isAxinomOwnedService && actualServiceStatus === ResourceStatus.Ready,
      {
        label: 'Undeploy',
        confirmationMode: 'Advanced',
        confirmationConfig: {
          body: 'The action is irreversible. By confirming the action the service deployment and its related data will be purged. Please confirm to proceed.',
        },
        onActionSelected: async () => {
          await envApolloClient.mutate<
            UndeployServiceMutation,
            UndeployServiceMutationVariables
          >({
            mutation: UndeployServiceDocument,
            variables: {
              input: {
                serviceDeploymentId: data?.serviceDeployments.nodes[0]?.id,
              },
            },
            fetchPolicy: 'no-cache',
          });
          history.replace(history.location.pathname);
        },
      },
    ),
    ...getActionsIf(!isAxinomOwnedService, {
      label: 'Deployments',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${serviceDefinitionId}/deployments`,
    }),
    {
      label: 'Deployment Logs',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${serviceDefinitionId}/logs`,
    },
    ...getActionsIf(!isAxinomOwnedService, {
      label: 'Container Images',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${serviceDefinitionId}/containerImages`,
    }),
    ...getActionsIf(!isAxinomOwnedService, {
      label: 'Pilet Artifacts',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${serviceDefinitionId}/piletArtifacts`,
    }),
    ...getActionsIf(!isAxinomOwnedService, {
      label: 'Deployment Manifests',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${serviceDefinitionId}/deployment-manifests`,
    }),
    ...getActionsIf(!databaseAccessOpen, {
      label: 'Open Database Access',
      isDisabled: !isDeployed,
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: (
          <span>
            This will allow developers with the required permission to connect
            directly to this service&apos;s database, where they can read and
            modify data. Access will close automatically once the granted period
            ends. Refer to {databaseAccessDocsLink} for more information. Please
            confirm to proceed.
          </span>
        ),
      },
      onActionSelected: async () => setDatabaseAccess(true),
    }),
    ...getActionsIf(databaseAccessOpen, {
      label: 'Close Database Access',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: (
          <span>
            This will close database access for this service. Any open sessions
            will be terminated and their temporary database credentials will be
            revoked. Refer to {databaseAccessDocsLink} for more information.
            Please confirm to proceed.
          </span>
        ),
      },
      onActionSelected: async () => setDatabaseAccess(false),
    }),
    ...getActionsIf(!isAxinomOwnedService, {
      label: 'Delete',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'Deleting a Customized Service is a destructive operation, and the active deployment (if any) must be un-deployed as a pre-requisite. Please confirm to proceed.',
      },
      onActionSelected: async () => {
        await envApolloClient.mutate<
          DeleteServiceDefinitionMutation,
          DeleteServiceDefinitionMutationVariables
        >({
          mutation: DeleteServiceDefinitionDocument,
          variables: {
            input: {
              id: serviceDefinitionId,
            },
          },
          fetchPolicy: 'no-cache',
        });
        history.replace(
          `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services`,
        );
      },
    }),
  ];

  return { actions } as const;
}

const getActionsIf = (
  condition: boolean,
  action: FormActionData<CustomizableService>,
): FormActionData<CustomizableService>[] => {
  if (!condition) {
    return [];
  }
  return [action];
};

export const CustomizableServiceDetailsCrumb: BreadcrumbResolver = ({
  envId,
  customizableServiceId,
}) => {
  return async (): Promise<string> => {
    const response = await environmentApolloClient(
      envId,
      new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
    ).query<CustomizableServiceCrumbDataQuery>({
      query: CustomizableServiceCrumbDataDocument,
      variables: { id: customizableServiceId },
      errorPolicy: 'ignore',
    });
    return (
      response.data.serviceDefinition?.name || 'Customizable Service Details'
    );
  };
};
